<script setup lang="ts">
import { nextTick, ref, watch } from 'vue';

const props = defineProps<{ busy: boolean; disabled: boolean; prefill?: string }>();

const emit = defineEmits<{
  send: [text: string];
  stop: [];
}>();

const text = ref('');
const textarea = ref<HTMLTextAreaElement | null>(null);

/** Grows with the content up to a few lines, then scrolls. */
async function resize(): Promise<void> {
  await nextTick();
  const element = textarea.value;
  if (element) {
    element.style.height = 'auto';
    element.style.height = `${Math.min(element.scrollHeight, 160)}px`;
  }
}

// Seeded from a ?prefill= link. Deliberately not auto-sent: nothing should be
// created, or even asked, without the person seeing it first.
watch(
  () => props.prefill,
  async (value) => {
    if (value) {
      text.value = value;
      await resize();
      textarea.value?.focus();
    }
  },
  { immediate: true },
);

function submit(): void {
  const value = text.value.trim();
  if (!value || props.busy || props.disabled) {
    return;
  }
  emit('send', value);
  text.value = '';
  void resize();
}
</script>

<template>
  <form class="composer" @submit.prevent="submit">
    <textarea
      ref="textarea"
      v-model="text"
      class="composer__input"
      rows="1"
      :disabled="disabled"
      :placeholder="
        disabled ? 'Stel eerst je API key in' : 'Beschrijf je zending…'
      "
      @input="resize"
      @keydown.enter.exact.prevent="submit"
    ></textarea>

    <button v-if="busy" class="btn" type="button" @click="emit('stop')">Stop</button>
    <button
      v-else
      class="btn btn--primary"
      type="submit"
      :disabled="disabled || text.trim().length === 0"
    >
      Verstuur
    </button>
  </form>
</template>

<style scoped>
.composer {
  display: flex;
  gap: 8px;
  align-items: flex-end;
  padding: 12px 16px 16px;
  border-top: 1px solid var(--border);
  background: var(--surface);
}

.composer__input {
  flex: 1;
  resize: none;
  min-height: 42px;
  max-height: 160px;
  line-height: 1.45;
}

/*
 * The textarea is one line tall until typing triggers resize(), so a placeholder
 * that wraps gets clipped mid-word at narrow widths. Keep placeholders short, and
 * let anything that still overflows degrade to an ellipsis rather than a cut-off.
 */
.composer__input::placeholder {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
